import {
    clearAllBodyScrollLocks,
    disableBodyScroll,
    enableBodyScroll,
} from "body-scroll-lock";
import classNames from "classnames";
import React from "react";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { Flair } from "../../../common/Flair";
import { Image } from "../../../common/Image";
import { Modal } from "../../../common/Modal";
import { ConfiguratorTypes } from "../../../constants";
import { IImageURL } from "../../../models/nominals";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { IModularConfiguratorPromoCardInfoModal } from "../models";
import { UpsellInfoColumnContent } from "./ConfiguratorUpsellInfoModalContent";

import styles from "./ConfiguratorPromoCardInfoModal.module.scss";

const getHeroAlt = (title: string | null): string => {
    return title ? title : "promo card - hero image";
};

const renderHeroImage = (
    infoModalImage: IImageURL | null | undefined,
    cardImage: IImageURL | null | undefined,
    heroAlt: string,
): React.ReactNode => {
    if (infoModalImage) {
        return (
            <Image
                className={styles.heroImage}
                src={infoModalImage}
                alt={heroAlt}
                lazy={false}
            />
        );
    }

    if (cardImage) {
        return (
            <Image
                className={styles.heroImage}
                src={cardImage}
                alt={heroAlt}
                lazy={false}
            />
        );
    }

    return null;
};

const renderHeroTitle = (
    infoModalTitle: string | null | undefined,
    title: string | null,
): React.ReactNode => {
    if (infoModalTitle) {
        return <h2 className={styles.heroTitle}>{infoModalTitle}</h2>;
    }

    if (title) {
        return <h2 className={styles.heroTitle}>{title}</h2>;
    }

    return null;
};

export const PromoCardInfoModal = (props: {
    configuratorType: ConfiguratorTypes;
    infoModal: IModularConfiguratorPromoCardInfoModal;
    isOpen: boolean;
    onCloseModal: () => void;
    modalTitle: string | null;
    title: string | null;
    cardImage: IImageURL | null | undefined;
}): React.ReactNode => {
    const modalContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
        };
    }, []);

    const onClosePanel = (): void => {
        enableBodyScroll(modalContainerRef.current as HTMLElement);
        const document = getDocument();
        if (document) {
            document.body.classList.remove("configurator-panel-open");
        }
    };

    const onOpenPanel = (): void => {
        const window = getWindow();
        if ((window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE) {
            disableBodyScroll(modalContainerRef.current as HTMLElement);
        }

        const document = getDocument();
        if (document) {
            document.body.classList.add("configurator-panel-open");
        }
    };

    const modalClasses = classNames({
        [styles.modalWrapper]:
            props.configuratorType !== ConfiguratorTypes.PANEL,
        [styles.panelWrapper]:
            props.configuratorType === ConfiguratorTypes.PANEL,
    });

    const overlayClasses = classNames({
        [styles.panelOverlay]:
            props.configuratorType === ConfiguratorTypes.PANEL,
    });

    const modalLabel = props.modalTitle ? props.modalTitle : "info modal";
    const heroAlt = getHeroAlt(props.title);

    return (
        <div ref={modalContainerRef}>
            <Modal
                overlayClassName={overlayClasses}
                className={modalClasses}
                isOpen={props.isOpen}
                onAfterOpen={onOpenPanel}
                onAfterClose={onClosePanel}
                onRequestClose={props.onCloseModal}
                contentLabel={modalLabel}
            >
                {props.modalTitle && (
                    <h1 className={styles.title}>{props.modalTitle}</h1>
                )}
                {props.infoModal.flair && props.infoModal.flair.length > 0 && (
                    <Flair
                        flair={props.infoModal.flair[0].value}
                        inline={true}
                    />
                )}
                <div className={styles.hero}>
                    {renderHeroImage(
                        props.infoModal.image,
                        props.cardImage,
                        heroAlt,
                    )}
                    {renderHeroTitle(props.infoModal.title, props.title)}
                </div>
                <div className={styles.container}>
                    <UpsellInfoColumnContent
                        isOneProduct={true}
                        content={props.infoModal.content}
                    />
                </div>
            </Modal>
        </div>
    );
};
